const { urlWithSearchParams } = require('./url');

/**
 * DIS (Dynamic Imaging Service) transformation parameters, keyed by the query
 * param name used in the image URL. All properties are optional.
 *
 * @see https://documentation.demandware.com/DOC2/topic/com.demandware.dochelp/ImageManagement/CreatingImageTransformationURLs.html
 * @typedef {object} ImageTransformations
 * @property {number} [sw] scaleWidth — output width in px (10–2000)
 * @property {number} [sh] scaleHeight — output height in px (10–2000)
 * @property {('cut'|'fit')} [sm] scaleMode — how the image fits the scale box
 * @property {number} [cx] cropX — crop origin X in px
 * @property {number} [cy] cropY — crop origin Y in px
 * @property {number} [cw] cropWidth — crop width in px
 * @property {number} [ch] cropHeight — crop height in px
 * @property {number} [q] quality — JPEG quality (1–100)
 * @property {('jpg'|'png'|'gif')} [sfrm] format — output image format
 */

/**
 * Valid DIS transformation query params. Used to validate the keys passed to
 * {@link applyImageTransformationsToUrl}. Keep in sync with the ImageTransformations
 * typedef above.
 * @type {ReadonlyArray<string>}
 */
const DIS_PARAM_KEYS = ['sw', 'sh', 'sm', 'cx', 'cy', 'cw', 'ch', 'q', 'sfrm'];

/**
 * Returns the image URL with the given DIS transformation parameters applied.
 *
 * Only the keys listed in {@link DIS_PARAM_KEYS} are forwarded; unknown keys are
 * ignored (with a warning). Entries whose value is null/undefined/'' are also
 * ignored, so an object with optional fields (e.g. `{ sw, sh }` without `q`) can
 * be passed without polluting the URL.
 *
 * @param {string} url Image URL (absolute or relative, usually a DIS base URL)
 * @param {ImageTransformations} [params={}] DIS transformation parameters
 * @returns {string} URL with the parameters applied (the original one if there are no valid params)
 */
function applyImageTransformationsToUrl(url, params = {}) {
  if (!url) return url;

  const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (!DIS_PARAM_KEYS.includes(key)) {
      // eslint-disable-next-line no-console
      console.warn(
        `applyImageTransformationsToUrl: ignoring unknown DIS param "${key}"`
      );
      return acc;
    }
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});

  if (Object.keys(cleanParams).length === 0) return url;
  return urlWithSearchParams(url, cleanParams);
}

/**
 * Builds a srcset string from a single base image URL and a list of widths,
 * applying each width as the DIS scaleWidth (sw) param. Lets the server return a
 * single base URL and compute the srcset on the client.
 *
 * @param {string} url Base image URL (usually a DIS base URL without sw)
 * @param {number[]} [widths=[]] Target widths in px (each becomes an `sw` param and a `Xw` descriptor)
 * @param {ImageTransformations} [extraParams={}] Extra DIS params applied to every entry (e.g. `{ q: 80 }`); `sw` is always set from each width
 * @returns {string} srcset value, e.g. "url?sw=200 200w, url?sw=400 400w"
 */
function getSrcsetFromUrl(url, widths = [], extraParams = {}) {
  if (!url || !Array.isArray(widths)) return '';
  return widths
    .filter((width) => Number.isFinite(width) && width > 0)
    .map((width) => {
      const transformedUrl = applyImageTransformationsToUrl(url, {
        ...extraParams,
        sw: width,
      });
      return `${transformedUrl} ${width}w`;
    })
    .join(', ');
}

module.exports = {
  applyImageTransformationsToUrl,
  getSrcsetFromUrl,
};
